import { ChangeDetectionStrategy, Component, Input, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, RouterLinkActive } from '@angular/router';

import { AuthService } from '../../../core/services/auth.service';
import { UserRole } from '../../../core/models/user.model';
import { SidebarNhom } from '../../../core/services/sidebar.service';

export interface EmaMenuItem {
  label: string;
  icon?: string;
  routerLink?: string | any[];
  active?: boolean;
  action?: () => void;
  roles?: UserRole[];
}

export interface EmaMenuSection {
  title: string;
  items: EmaMenuItem[];
}

@Component({
  selector: 'app-ema-shell',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="ema-shell-root">
      <header class="ema-topbar">
        <button class="hamburger" (click)="toggleMenu()" aria-label="Menu">
          <span></span><span></span><span></span>
        </button>
        <div class="title">{{ title }}</div>
        <div class="ctx-badges">
          @for (b of badges; track b) { <span class="ctx-pill">{{ b }}</span> }
        </div>
        @if (authService.currentUser(); as user) {
          <div class="topbar-user">
            <span class="topbar-name">{{ user.tennhanvien }}</span>
            <span class="topbar-role">{{ user.tenvaitro }}</span>
          </div>
        }
      </header>

      <div class="ema-body">
        <aside class="ema-slidemenu" [class.open]="menuOpen()">
          @for (nhom of menuNhom; track nhom.nhom_id) {
            <div class="mgroup">
              <button type="button" class="mgroup-header" (click)="toggleGroup(nhom.ma_nhom)">
                <span class="mgroup-left">
                  <i [class]="iconForGroup(nhom.ma_nhom)" aria-hidden="true"></i>
                  {{ nhom.ten_nhom }}
                </span>
                <i class="pi pi-chevron-right mgroup-chevron"
                   [class.open]="isGroupOpen(nhom.ma_nhom)"
                   aria-hidden="true"></i>
              </button>

              @if (isGroupOpen(nhom.ma_nhom)) {
                @for (item of nhom.manhinh; track item.manhinh_id) {
                  <a class="mitem"
                     [routerLink]="item.path_manhinh"
                     routerLinkActive="active"
                     [routerLinkActiveOptions]="{ exact: false }">
                    <span>{{ item.ten_manhinh }}</span>
                  </a>
                }
              }
            </div>
            <div class="mdiv"></div>
          }

          @for (sec of sections; track sec.title) {
            <div class="mgroup">
              <div class="mhead">{{ sec.title }}</div>
              @for (it of sec.items; track it.label) {
                @if (it.action) {
                  <button type="button" class="mitem mitem-btn" (click)="it.action()">
                    <span class="icon">{{ it.icon }}</span>
                    <span>{{ it.label }}</span>
                  </button>
                } @else if (it.routerLink) {
                  <a class="mitem"
                     [routerLink]="it.routerLink"
                     routerLinkActive="active"
                     [routerLinkActiveOptions]="{ exact: false }">
                    <span class="icon">{{ it.icon }}</span>
                    <span>{{ it.label }}</span>
                  </a>
                }
              }
            </div>
          }
        </aside>

        <main class="ema-content" [class.menu-open]="menuOpen()">
          <ng-content></ng-content>
        </main>
      </div>
    </div>
  `,
  styleUrl: './ema-shell.component.scss',
})
export class EmaShellComponent {
  readonly authService = inject(AuthService);

  @Input() title = 'EMA - Quản lý sức khỏe cộng đồng';
  @Input() badges: string[] = [];
  @Input() sections: EmaMenuSection[] = [];
  @Input() menuNhom: SidebarNhom[] = [];
  @Input() defaultMenuOpen = true;

  readonly nhomIcons: Record<string, string> = {
    tongquan: 'pi pi-home',
    benhnhan: 'pi pi-users',
    quanlycongviec: 'pi pi-sitemap',
    dulieu: 'pi pi-chart-bar',
    tonghopdulieu: 'pi pi-database',
    cauhinh: 'pi pi-cog',
    hethong: 'pi pi-server',
  };

  readonly groupOpen = signal<Record<string, boolean>>({});
  readonly menuOpen = signal(true);

  ngOnInit(): void {
    this.menuOpen.set(this.defaultMenuOpen);
  }

  toggleMenu(): void {
    this.menuOpen.update((v) => !v);
  }

  toggleGroup(ma_nhom: string): void {
    this.groupOpen.update((s) => ({ ...s, [ma_nhom]: !(s[ma_nhom] ?? true) }));
  }

  isGroupOpen(ma_nhom: string): boolean {
    const s = this.groupOpen();
    return ma_nhom in s ? s[ma_nhom] : true;
  }

  iconForGroup(ma_nhom: string): string {
    return this.nhomIcons[ma_nhom] || 'pi pi-circle';
  }
}
